import { FC, useState } from 'react';

import { Button, Icon, Modal, Whiteboard, FileUploader } from '@components';
import { QuestionAttachment, QuestionType } from '@types';
import { showAttachment } from '@features/homeworks/utils';
import safeParseHtml from '@utils/safeParseHtml';
import colors from '../../../../../themes/colors';
import MathAnswerInput from '../math-answer-input/MathAnswerInput';

interface ShortAndLongAnswerProps {
  question: any;
  onInputChange: (value: string) => void;
  uploadedFiles: any;
  setUploadedFiles: (value: any) => void;
  parentRef: any;
  disableInput?: boolean;
  answer: string;
  isCanvasRequired?: boolean;
}

const ShortAndLongAnswer: FC<ShortAndLongAnswerProps> = ({
  question,
  onInputChange,
  uploadedFiles,
  setUploadedFiles,
  disableInput = false,
  parentRef,
  answer,
  isCanvasRequired = true
}) => {
  const [isWarningModal, setIsWarningModal] = useState(false);
  const [saveCanvasData, setSaveCanvasData] = useState<string | null>(null);
  const [isFileUploadOpen, setIsFileUploadOpen] = useState<boolean>(false);
  const isMathSubject = question?.subjects?.name?.toLowerCase() === 'maths';
  // TODO: might need to change this to a prop for different subject
  const isGridView = false;

  const removeImage = (index: number) => {
    const updatedArray = [...uploadedFiles];
    updatedArray.splice(index, 1);
    setUploadedFiles(updatedArray);
  };

  const handleFileUploadModal = () => {
    if (saveCanvasData && saveCanvasData.length) {
      setIsWarningModal(true);
    } else {
      setIsFileUploadOpen(true);
    }
  };

  const handleProceedFileUpload = () => {
    setIsWarningModal(false);
    setIsFileUploadOpen(true);
  };

  const handleCloseWarningModal = () => {
    setIsWarningModal(false);
  };

  const handleCloseFileUploadModal = () => {
    setIsFileUploadOpen(false);
  };

  return (
    <div className={isGridView ? 'sm:grid sm:grid-cols-2' : ''}>
      {/* <div
        className={`${
          isGridView
            ? 'pr-2 sm:max-h-[calc(100vh-400px)] sm:overflow-y-auto sm:overflow-x-hidden'
            : ''
        }`}
      > */}
      <div
        className={`${
          isGridView ? 'pr-2 sm:overflow-visible sm:overflow-x-hidden' : ''
        }`}
      >
        {question?.questionDetails && (
          <>
            <div className="mb-3 w-fit text-xl font-bold text-primary">
              {question?.questionDetails?.title}
            </div>
            <div
              className={`mb-12 w-3/5 ${
                isGridView ? 'w-full' : ''
              } text-lg font-semibold`}
            >
              {safeParseHtml(question?.questionDetails?.text ?? '')}
            </div>
            <div className="mb-3 w-fit text-xl font-bold text-primary">
              Question
            </div>
          </>
        )}
        <div className="mb-3 mt-2 text-xl font-bold text-primary">
          {question.title ?? 'Question'}
        </div>
        {/* <div className="mb-12 w-3/5 text-lg font-semibold"> */}
        <div className="mb-6 w-full text-lg font-semibold">
          {safeParseHtml(question?.question ?? '')}
        </div>
        <div className="mx-1 flex w-full flex-col gap-4">
          {question?.questionAttachments?.map(
            (attachment: QuestionAttachment) => (
              <div key={attachment.id} className="w-full">
                {showAttachment(attachment, true)}
              </div>
            )
          )}
        </div>
      </div>
      <div
        className={`${
          isGridView
            ? 'sm:-mt-63 sm:overflow-visible sm:overflow-x-hidden sm:ps-10'
            : 'mt-4'
        } ${
          question?.questionDetails?.title ? 'sm:!-mt-6' : ''
        } flex cursor-pointer flex-col gap-4`}
      >
        {/* <div className="mb-3 mt-6 text-xl font-bold text-primary"> */}
        <div className="mb-3 mt-2 text-xl font-bold text-primary">
          {question.title ?? 'Answer'}
        </div>
        <MathAnswerInput
          value={answer}
          onChange={onInputChange}
          multiline={question.type === QuestionType.LONG_ANSWER}
          placeholder="Enter answer"
          disabled={disableInput}
          isPasteAllowed={false}
          showMathToolbar={isMathSubject}
          rows={2}
          className={
            question.type === QuestionType.LONG_ANSWER
              ? `p-2 ${isGridView ? 'h-40' : ''}`
              : 'h-12 w-4/5 min-w-48 rounded-2xl border border-solid border-grey1 p-3'
          }
        />
        {isCanvasRequired && (
          <div className="mr-6">
            <div className="mb-3 mt-4 text-xl font-bold text-primary">
              Work Sheet
            </div>
            <div className="relative min-h-235 max-w-full rounded-2xl">
              <Whiteboard
                ref={parentRef}
                id="long answer worksheet"
                className="long-answer min-h-300 w-full"
                buttonStyles="w-16 h-8 bg-primary5 text-white semibold rounded-md cursor-pointer"
                strokeWidth={3}
                strokeColor="#000000"
                disableAllButtons
                setSaveCanvasData={setSaveCanvasData}
                canvasClassName=" rounded-2xl border border-solid border-grey1 !size-full"
                isSaveButton={false}
                isClearCanvasButton
                isEraserButton
                isUndoButton
                isRedoButton
                handleUpload={handleFileUploadModal}
              />
              <div className="mt-3 flex items-center justify-between rounded-b-2xl border-t border-grey1 px-4 py-3">
                <div className="flex flex-1 gap-2 overflow-x-auto">
                  {!!uploadedFiles.length &&
                    uploadedFiles.map((file: any, index: number) => (
                      <div className="relative h-24 w-24 flex-shrink-0 overflow-hidden rounded-lg border border-neutral5">
                        <img
                          src={URL.createObjectURL(file)}
                          alt="preview"
                          className="h-full w-full object-cover"
                          onLoad={() => URL.revokeObjectURL(file)}
                        />

                        <Icon
                          name="rounded-close"
                          stroke={colors.grey7}
                          size="large"
                          className="absolute right-1 top-1 cursor-pointer rounded-full bg-white"
                          onClick={() => removeImage(index)}
                        />
                      </div>
                    ))}
                </div>

                {/* <button
                  type="button"
                  onClick={handleFileUploadModal}
                  className="ml-4 flex h-11 w-11 items-center justify-center rounded-lg transition-colors hover:bg-grey100"
                >
                  <Icon
                    name="attachment-pin"
                    stroke={colors.warning500}
                    size="extralarge"
                  />
                </button> */}
              </div>
              {/* <div
                className={clsx(
                  'absolute  right-5',
                  !uploadedFiles.length ? 'bottom-20' : 'bottom-5'
                )}
              >
                <Icon
                  name="attachment-pin"
                  stroke={colors.warning500}
                  fill=""
                  size="extralarge"
                  className="cursor-pointer"
                  onClick={handleFileUploadModal}
                />
              </div> */}
            </div>
          </div>
        )}
      </div>
      <Modal
        isOpen={isFileUploadOpen}
        isCloseIconRequired
        onCancel={handleCloseFileUploadModal}
      >
        <FileUploader
          disabled={false}
          id="upload files"
          name="upload files"
          setUploadedFiles={(files) => setUploadedFiles(files)}
          fileCriteriaMsgs="Upload Image Files"
          accept="image/png,image/jpeg,.jpeg,.jpg,.png"
          uploadBtnText="Upload File"
          fileType="image"
          onUploadCancel={handleCloseFileUploadModal}
          uploadedFiles={uploadedFiles}
        />
      </Modal>
      <Modal
        isOpen={isWarningModal}
        isCloseIconRequired
        onCancel={handleCloseWarningModal}
      >
        <div className="flex flex-col p-12">
          <div className="m-4 text-center text-base">
            Uploading image can lost data on worksheet <br /> Do you want to
            continue ?
          </div>
          <div className="mt-5 flex w-full items-center justify-around gap-4">
            <Button
              label="Cancel"
              variant="secondary"
              handleClick={handleCloseWarningModal}
              className="min-w-100"
            />
            <Button
              label="Continue"
              className="min-w-100"
              handleClick={handleProceedFileUpload}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ShortAndLongAnswer;
